/**
 * Questions the assistant could not answer.
 *
 * Two sources: an answer where it said as much, and a question that became a
 * ticket — the second being proof rather than inference. Either way it reads
 * as a list of things the guides do not yet cover.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { HelpCircle, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';

const REASON = {
  escalated: { label: 'Became a ticket', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  unanswered: { label: 'Assistant could not answer', tone: 'bg-slate-100 text-slate-600 border-slate-200' },
};

export const GuideGaps = () => {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/platform/guide-gaps`, {
        params: { days: 60 }, headers: authHeader(),
      });
      setData(d);
    } catch { setData(null); } finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (id) => {
    try {
      await axios.delete(`${API}/platform/guide-gaps/${id}`, { headers: authHeader() });
      setData((d) => ({ ...d, gaps: d.gaps.filter((g) => g.id !== id) }));
    } catch { toast.error('Could not clear that one'); }
  };

  return (
    <section className="mt-10" data-testid="guide-gaps">
      <h2 className="text-base font-semibold tracking-tight text-slate-900 inline-flex items-center gap-2">
        <HelpCircle size={15} className="text-amber-600" /> What the guides do not cover
      </h2>
      <p className="text-xs text-slate-500 mt-0.5">
        {data
          ? `${data.gaps.length} in the last ${data.days} days, ${data.gaps.filter((g) => g.reason === 'escalated').length} of which became tickets.`
          : 'Loading…'}{' '}
        Clear one once you have written it into a guide.
      </p>

      {loading ? (
        <div className="p-8 grid place-items-center"><Loader2 className="animate-spin text-slate-300" size={18} /></div>
      ) : (
        <div className="mt-3 bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
          {(data?.gaps || []).map((g) => (
            <div key={g.id} className="px-4 py-3 flex items-start gap-3" data-testid={`gap-${g.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-900">{g.question}</p>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <span className={`text-[9px] font-semibold uppercase tracking-wider border rounded-full px-1.5 py-0.5 ${REASON[g.reason]?.tone || REASON.unanswered.tone}`}>
                    {REASON[g.reason]?.label || g.reason}
                  </span>
                  <span className="text-[10px] text-slate-400">
                    {new Date(g.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    {g.screen ? ` · from ${g.screen}` : ''}
                  </span>
                </div>
              </div>
              <button
                onClick={() => dismiss(g.id)}
                title="Clear"
                className="shrink-0 text-slate-300 hover:text-slate-600"
                data-testid={`gap-dismiss-${g.id}`}
              >
                <X size={14} />
              </button>
            </div>
          ))}
          {!data?.gaps?.length && (
            <p className="px-4 py-6 text-sm text-slate-500 text-center" data-testid="guide-gaps-empty">
              Nothing flagged. Every question so far was answered from the guides.
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default GuideGaps;
